import { Phone, Star } from 'lucide-react'
import type { FC, MouseEvent } from 'react'
import { Link, type To } from 'react-router'
import { pluralize } from '../plural'
import { formatCredentials } from '../provider-name'
import { Avatar } from './avatar'
import { DollarRating } from './dollar-rating'
import { PinIcon } from './icons'
import { formatPhoneNumber, formatTelHref } from './phone'

/** Fields from `searchProviders` recommendations used by the results card. */
export type ProviderCardProvider = {
  name?: string | null | undefined
  credentials?: string | null | undefined
  specialty?: string | null | undefined
  avatarUrl?: string | null | undefined
  distanceInMiles?: number | null | undefined
  facilityName?: string | null | undefined
  address?: string | null | undefined
  rating?: number | null | undefined
  ratingsCount?: number | null | undefined
  efficiencyIndex?: number | null | undefined
  phone?: string | null | undefined
}

type ProviderCardProps = {
  provider: ProviderCardProvider
  /** Relative provider-detail route opened by the card body and “More Info”. */
  detailTo: To
}

/** First letters of the first two name words, uppercased — the avatar fallback. */
export const toInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('')

export const ProviderCard: FC<ProviderCardProps> = ({ provider, detailTo }) => {
  const {
    name,
    credentials,
    avatarUrl,
    distanceInMiles,
    facilityName,
    address,
    rating,
    ratingsCount,
    efficiencyIndex,
    phone,
  } = provider

  const hasFacilityName = facilityName != null && facilityName.trim() !== ''
  const hasAddress = address != null && address.trim() !== ''
  const hasLocationSummary = distanceInMiles != null || hasFacilityName || hasAddress
  // Trimmed first so a whitespace-only number counts as absent: formatPhoneNumber
  // only returns null for null/empty, and would hand back the blank string.
  const trimmedPhone = phone?.trim() ?? ''
  const displayPhone = trimmedPhone === '' ? null : (formatPhoneNumber(trimmedPhone, false) ?? trimmedPhone)
  // The heading text, split so the credentials can be styled apart from the name:
  // "Patricia Olson" + ", PhD, MD". The suffix is '' when there are no credentials
  // — or when they're whitespace-only — so the comma can never render alone.
  //
  // Gated on the name too. `renderable` in results.tsx already drops nameless
  // providers, so today no card reaches this without one; the guard is what stops
  // an <h2> reading ", MD" if another caller ever skips that filter, and it keeps
  // the heading agreeing with the avatar's alt text, which is empty in that case.
  const trimmedName = name?.trim() ?? ''
  const credentialsSuffix = trimmedName === '' ? '' : formatCredentials(credentials)

  const keepMoreInfoClickLocal = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation()
  }

  return (
    // `w-full` because the card is now a flex child of the grid's <li> rather than
    // a grid item itself (see the results grid): the row still stretches it
    // vertically, but the width has to be claimed.
    <article
      className="flex w-full flex-col gap-3 rounded-2xl border border-secondary-200 bg-white has-hover:ring has-hover:ring-primary-400 has-hover:border-primary-400 has-hover:hover:shadow-lg shadow-secondary-800/10 transition-all duration-200"
      data-testid="provider-card"
    >
      <Link
        data-testid="provider-card-link"
        prefetch="viewport"
        to={detailTo}
        className="flex flex-col gap-3 no-underline text-inherit px-4 pt-4"
      >
        <header className="flex items-start gap-3">
          <Avatar url={avatarUrl} name={name} credentials={credentials} />
          <div className="min-w-0 flex-1">
            <h2
              className="truncate font-display text-xl font-semibold leading-tight text-secondary-800"
              data-testid="provider-card-name"
            >
              {/* `trimmedName`, not `name`: the suffix is built from the trimmed
                  value, so rendering the raw one puts the stray whitespace back
                  between them — "Jane Doe , MD" — and makes the heading disagree
                  with the avatar's alt text, which formatProviderName trims. */}
              {trimmedName}
              {/* The separator is in the text, not in the margin (VIT-18200). The
                  old `ml-2` left the h2's textContent reading "Patricia OlsonPhD,
                  MD" — which is what Google scraped into the SERP snippet, and what
                  a screen reader announced.

                  No margin at all now: the suffix leads with ", ", so a margin on
                  top of it renders "Lena Park , MD" with the comma floating off the
                  name. The space after the comma is the gap. */}
              {credentialsSuffix !== '' && (
                <span className="text-xs font-sans font-normal text-secondary-600">{credentialsSuffix}</span>
              )}
            </h2>
            {(typeof rating === 'number' || efficiencyIndex != null) && (
              <div className="flex items-center gap-4 mt-1">
                {typeof rating === 'number' && (
                  // Without the label a screen reader reads the star (decorative),
                  // the number and the parenthesised count as "4.5 120" — three
                  // unrelated facts (VIT-17746). The label carries the whole
                  // statement and the visible text stays as designed.
                  //
                  // `role="img"` is what makes the label take effect at all: an
                  // aria-label on a plain <div> names nothing, because a generic
                  // role doesn't support naming, so AT ignores it and reads the
                  // children anyway. The role also hides those children, which is
                  // what we want — the label restates them.
                  <div
                    role="img"
                    className="flex items-center gap-1"
                    data-testid="provider-card-ratings"
                    aria-label={
                      ratingsCount != null
                        ? `Rated ${rating.toFixed(1)} out of 5 from ${ratingsCount} ${pluralize(ratingsCount, 'review')}`
                        : `Rated ${rating.toFixed(1)} out of 5`
                    }
                  >
                    <Star aria-hidden="true" className="size-4 stroke-none fill-primary-500" />
                    <span className="text-sm font-semibold text-secondary-800">{rating.toFixed(1)}</span>
                    {ratingsCount != null && <span className="text-sm text-secondary-700">({ratingsCount})</span>}
                  </div>
                )}
                {efficiencyIndex != null && (
                  <div className="flex items-center gap-1.5" data-testid="provider-card-cost">
                    <span className="text-sm text-secondary-800">Cost:</span>
                    <DollarRating efficiencyIndex={efficiencyIndex} className="text-sm font-semibold leading-none" />
                  </div>
                )}
              </div>
            )}
          </div>
        </header>

        {hasLocationSummary && (
          <div className="flex min-w-0 items-start gap-2">
            <PinIcon className="size-6 shrink-0 text-primary-500" />
            <div className="-mt-0.5 min-w-0 flex-1 line-clamp-2">
              {distanceInMiles != null && (
                <span
                  className="whitespace-nowrap text-sm font-bold text-secondary-800"
                  data-testid="provider-card-distance"
                >
                  {distanceInMiles.toFixed(1)} Mi&nbsp;&nbsp;
                </span>
              )}
              {hasFacilityName && (
                <span className="text-sm font-semibold text-secondary-700" data-testid="provider-card-facility-name">
                  {facilityName}
                </span>
              )}
              {hasAddress && (
                <div className="text-sm text-secondary-600" data-testid="provider-card-address">
                  {address}
                </div>
              )}
            </div>
          </div>
        )}
      </Link>

      <div className="mt-auto flex flex-wrap gap-2 px-4 pb-4" data-testid="provider-card-actions">
        {displayPhone != null && (
          // A real `tel:` anchor, not a <button> assigning window.location
          // (VIT-17746): the anchor is crawlable, announced as a link with the
          // number as its name, and works with the keyboard and with
          // open-in-new-tab. Built from `displayPhone`, exactly as the detail page
          // does — formatTelHref normalises a display string. Outside the card's
          // <Link>, so no click needs intercepting.
          <a
            href={formatTelHref(displayPhone)}
            className="w-full xs:w-auto btn btn-secondary group"
            data-testid="provider-card-phone"
          >
            <Phone aria-hidden="true" className="size-5 stroke-0 fill-primary-500 group-hover:fill-white" />
            {displayPhone}
          </a>
        )}
        <Link
          to={detailTo}
          prefetch="viewport"
          onClick={keepMoreInfoClickLocal}
          data-testid="provider-card-more-info"
          className="w-full xs:w-auto btn btn-primary btn-outline"
        >
          More Info
        </Link>
      </div>
    </article>
  )
}
